// Submitting a loan file to Doorly — the loan officer's side of the wholesale channel.
//
// 🔑 LO PLAN v7 (Jesse, 2026-10-28): three tabs — New, Drafts n, Submitted n — and the submission as a guided flow
// inside DoorlyPro (components/doorlypro/submission/LoSubmissionFlow), saved on the server on every Continue. New shows
// only the two start cards; a draft lives under Drafts with its step, Continue and its deletion date; a submitted file
// opens its own read-only page (/doorlypro/submissions/:id). After submit the LO has no part in the borrower's
// application: no upload panel, nothing sent back.
//
// ⚠️ THERE ARE NO FIELD NAMES IN THIS FILE. The flow asks GET /submissions/schema what to render.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import SubmissionTabs from '../../components/doorlypro/submission/SubmissionTabs';
import LoSubmissionFlow from '../../components/doorlypro/submission/LoSubmissionFlow';
import '../../components/doorlypro/submission/loSubmission.css';
import { withReturn } from '../../components/doorlypro/ReadinessChecklist';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🚨 THE ONLY COLOURS THIS FORM USES. The sections carried invented hexes —
// #1d6b45, #7a5200, #6d28d9, #b3321f — none of which are Doorly colours. Jesse:
// "only use the Doorly approved primary brand colors." The server now sends a
// TOKEN NAME and this resolves it against index.css, which is where the palette
// is defined once.
//
// ⚠️ AND THE SECTIONS WERE ONLY THE PART SOMEBODY HAD LOOKED AT. The Submitted
// panel, the served/not-served badge and the down-payment card kept the same
// invented green ramp for another round, because the test guarding this was
// scoped to the map below rather than to the file. It now covers the file —
// see backend/test/brandPalette.test.js. Where a green was a FILL or a BORDER
// it became --color-success; where it was TEXT it became
// --color-success-text, because #acf44e on white is about 1.6:1 and the dark
// green the old palette reached for is exactly how this started.
//
// ⚠️ AN UNKNOWN TOKEN FALLS BACK TO BRAND BLUE rather than to a literal, so a
// section added with a token nobody defined is on-brand and visibly wrong in
// the same way as its neighbours — not a stray colour.
const ACCENTS = {
  blue: 'var(--color-brand-blue)',
  'blue-dark': 'var(--color-brand-blue-dark)',
  'blue-light': 'var(--color-brand-blue-light)',
  navy: 'var(--color-brand-navy)',
  gold: 'var(--color-brand-gold)',
  // 🔑 THE DOORLY GREEN AND YELLOW ARE THE ONES index.css ALREADY DECLARES —
  // --color-success is the brand lime used for every success state, and
  // --color-brand-yellow sits in the same primary block as the blue. Neither
  // needed defining; picking a new hex for "green" is how the four invented
  // section colours happened.
  green: 'var(--color-success)',
  yellow: 'var(--color-brand-yellow)',
};
const accentOf = (token) => ACCENTS[token] || ACCENTS.blue;
// The accent the start cards' selected border takes — the brand blue, from the map above.
const SELECTED = accentOf('blue');

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateLabel = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? '' : `${MON[x.getMonth()]} ${x.getDate()}, ${x.getFullYear()}`; };

// 🔑 BEHIND THE SIGN-IN CHECK, LIKE EVERY OTHER DoorlyPro PAGE (broker report 1102, Jesse 2026-11-02). This page was
// routed bare: a signed-out visitor got the partner shell and its requests went out with a null token (each refused by
// the server — verifyPartnerToken — but the page itself never sent them to sign in). RequirePartnerAuth holds the render
// until the session is read and sends a signed-out visitor to /doorlypro/login; the page's own hooks run only after.
export default function DoorlyProSubmissions() {
  return (
    <RequirePartnerAuth>
      <DoorlyProSubmissionsPage />
    </RequirePartnerAuth>
  );
}

function DoorlyProSubmissionsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countersignTask, setCountersignTask] = useState(null);
  const [list, setList] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [format, setFormat] = useState('manual');
  const [starting, setStarting] = useState(false);
  const [startErr, setStartErr] = useState(null);
  const agreementBlockedRef = React.useRef(false);

  const token = localStorage.getItem('doorlypro_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const tab = ['new', 'drafts', 'submitted'].includes(params.get('tab')) ? params.get('tab') : 'new';
  const draftId = params.get('draft');

  const refreshLists = useCallback(() => {
    fetch(`${API_URL}/api/doorlypro/submissions`, { headers })
      .then((r) => (r.ok ? r.json() : { submissions: [] })).then((d) => setList(d.submissions || [])).catch(() => {});
    fetch(`${API_URL}/api/doorlypro/submissions/drafts`, { headers })
      .then((r) => (r.ok ? r.json() : { drafts: [] })).then((d) => setDrafts(d.drafts || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/doorlypro/submissions/schema`, { headers })
      .then((r) => r.json()).then(setSchema).catch(() => setSchema({ allowed: false }))
      .finally(() => setLoading(false));
    // 🔑 THE SERVER'S OWN TASK (getPartnerTaskState) — the counter-signature row only, informational.
    // ⚠️ EXACTLY ONE TASK, AND THE NARROWNESS IS THE DESIGN: the agreement overlay returns a partner here past the
    // dashboard, so this one row is carried. A general task rail on this page is its own decision.
    fetch(`${API_URL}/api/doorlypro/onboarding`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const t = (d?.task_state?.tasks || []).find((x) => x.key === 'countersigned' && !x.done);
        setCountersignTask(t || null);
      })
      .catch(() => setCountersignTask(null));
    refreshLists();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!draftId) refreshLists(); }, [draftId, tab, refreshLists]);

  const setTab = (t) => setParams({ tab: t });
  const start = async () => {
    if (agreementBlockedRef.current) return;
    setStarting(true); setStartErr(null);
    try {
      const r = await fetch(`${API_URL}/api/doorlypro/submissions/draft`, { method: 'POST', headers, body: JSON.stringify({ state: { values: {} }, step_key: 'about' }) });
      const d = await r.json();
      if (!r.ok) { setStartErr(d.message || d.error || 'Your submission could not be started.'); return; }
      setParams({ draft: String(d.draft.id), step: 'about' });
    } catch (_) { setStartErr('Could not reach Doorly. Try again.'); } finally { setStarting(false); }
  };

  if (loading) return <DoorlyProLayout><div style={{ padding: 40, color: '#6b7280' }}>Loading…</div></DoorlyProLayout>;

  const setupRefusal = !schema?.allowed
    && (schema?.refusal?.error === 'AGREEMENT_NOT_EXECUTED'
      || schema?.refusal?.error === 'PARTNER_NOT_ACTIVE')
    ? schema.refusal : null;
  const agreementBlocked = !!setupRefusal;
  agreementBlockedRef.current = agreementBlocked;

  // 🚨 THE CAPABILITY REFUSAL, EXPLAINED RATHER THAN STONEWALLED. The nav item is
  // hidden for accounts without the flag, so anyone here followed a direct link —
  // and the drafted copy already says what is missing and what staff would do.
  //
  // ⚠️ THE AGREEMENT CASE DOES NOT COME HERE. It is "not yet" rather than "not
  // your product", and it renders the page with the overlay instead.
  if (!schema?.allowed && !agreementBlocked) {
    return (
      <DoorlyProLayout>
        <div style={{ maxWidth: 620, margin: '0 auto', padding: '40px 20px' }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 12px' }}>Submitting applications</h1>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
            <p style={{ margin: 0, fontSize: 15, color: '#374151', lineHeight: 1.7 }}>
              {schema?.refusal?.message
                || 'Your account is not set up to submit loan applications to Doorly yet.'}
            </p>
            {schema?.refusal?.staffAction && (
              <p style={{ margin: '14px 0 0', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                {schema.refusal.staffAction}
              </p>
            )}
          </div>
        </div>
      </DoorlyProLayout>
    );
  }

  // ── 🔑 A SUBMISSION IN PROGRESS: the guided flow, inside the layout (the sidebar stays). ──
  if (draftId) {
    return (
      <DoorlyProLayout>
        {overlayNode()}
        <LoSubmissionFlow key={draftId} token={token} schema={schema} draftId={Number(draftId)} initialStep={params.get('step') || null}
          blocked={agreementBlocked}
          onExit={(t) => setParams({ tab: t || 'drafts' })}
          onStep={(s) => setParams({ draft: String(draftId), step: s }, { replace: true })}
          onSubmitted={(id) => navigate(`/doorlypro/submissions/${id}`)} />
      </DoorlyProLayout>
    );
  }

  function overlayNode() {
    return (
      <>
      {agreementBlocked && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="agreement-required-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 900,
            background: 'rgba(17, 24, 39, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          /* ⚠️ NO onClick DISMISS AND NO onKeyDown ESCAPE. Their absence is the
             feature; do not add them "for consistency" with other modals. */
        >
          <div style={{ background: '#fff', borderRadius: 14, maxWidth: 460, width: '100%', padding: '28px 26px', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
            {/* 🔑 THE HEADING NAMES THE WHOLE CONDITION, NOT ONE ITEM OF IT. It
                said "Complete your Wholesale Broker Agreement" for every setup
                refusal — so a partner who had signed and still owed training
                read a heading about a thing they had already done. */}
            <h2 id="agreement-required-title" style={{ fontSize: 19, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>
              {setupRefusal?.error !== 'PARTNER_NOT_ACTIVE'
                ? 'Complete your Wholesale Broker Agreement'
                /* ⚠️ "A few things to finish first" CLAIMS THE WORK IS THEIRS,
                   and in the licence-pending state it is not — profile and
                   training are done and Doorly is still reading the register.
                   With no items on their side the heading says only that the
                   account is not active; the notice below says whose turn it
                   is. */
                : (setupRefusal.items || []).length
                  ? 'A few things to finish first'
                  : 'Your account isn\u2019t active yet'}
            </h2>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, margin: '0 0 8px' }}>
              {setupRefusal?.message
                || 'We can\'t accept an application until your Partner Agreement is signed.'}
            </p>

            {/* ── 🚨 EVERY OUTSTANDING ITEM, IN THE SERVER'S OWN WORDS ────────
                One sentence naming one of three outstanding things is how a
                partner finishes that one and comes straight back to the same
                overlay. The rows and their wording come from
                getPartnerTaskState — the same source the dashboard and Eva
                read — so this cannot describe a different setup from the one
                behind it. */}
            {(setupRefusal?.items || []).length > 0 && (
              <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13.5,
                color: '#374151', lineHeight: 1.75 }}>
                {setupRefusal.items.map((t) => (
                  <li key={t.key} data-testid={`overlay-item-${t.key}`} style={{ marginBottom: 6 }}>
                    {t.label}
                    {/* ⚠️ A ROW THAT IS NOT THEIRS IS MARKED, not hidden, and
                        says whose it is (push C wording). Hiding it would make
                        the list look shorter than the wait; showing it as a
                        to-do would tell them to do something they cannot. */}
                    {t.not_yours && (
                      <span style={{ color: '#6b7280' }}> {'\u2014'} Doorly is doing this, no action needed</span>
                    )}
                    {/* 🔑 EVERY ROW OF THEIRS HAS ITS OWN BUTTON (push C), going
                        straight to where it is done and back here after. One
                        button for the whole list is how a partner finished the
                        first item and lost the others. */}
                    {!t.not_yours && t.where && (
                      <button type="button" data-testid={`overlay-go-${t.key}`}
                        onClick={() => { window.location.href = withReturn(t.where, '/doorlypro/submissions'); }}
                        style={{ marginLeft: 8, padding: '3px 10px', background: '#fff', color: '#3a6ad9',
                          border: '1px solid #3a6ad9', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        {t.key === 'agreement' ? 'Sign now' : t.key === 'training' ? 'Start training' : 'Go'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* 🔑 THE LICENCE IS NOT A TASK ROW, because it is not theirs. The
                server's sentence is the true one for the state they are in:
                "we're verifying" while it is Doorly's step, the specific finding
                once the register has answered negatively. */}
            {setupRefusal?.notice && (
              <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7,
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                padding: '9px 12px', margin: '0 0 12px' }}>
                {setupRefusal.notice}
              </p>
            )}

            {/* ⚠️ THE "you can read what a submission asks for on the page
                behind this" LINE IS GONE, by instruction. The preview itself
                is unchanged — the schema endpoint still returns the field list
                for both setup refusals — so the form is still there to read;
                what is removed is the sentence directing attention to it,
                which competed with the list of things to actually do. */}
            <div style={{ height: 14 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              {/* 🔑 RETURNS HERE AFTER SIGNING, not to the dashboard. Signing and
                  continuing is one motion; the agreement page reads this and
                  navigates back. */}
              {/* ⚠️ THE BUTTON GOES WHERE THE NEXT ITEM IS, and says so. It
                  said "Complete agreement" and went to the agreement for every
                  setup refusal — including for a partner whose agreement was
                  already signed. The server names the destination. */}
              <button
                onClick={() => {
                  // ⚠️ where MAY ALREADY CARRY A QUERY (?training=1), so the
                  // return is joined with & then (push C).
                  const w = setupRefusal?.where || '/doorlypro/onboarding/agreement';
                  window.location.href = `${w}${w.includes('?') ? '&' : '?'}return=${encodeURIComponent('/doorlypro/submissions')}`;
                }}
                style={{ flex: 1, padding: '11px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                {setupRefusal?.error === 'PARTNER_NOT_ACTIVE' ? 'Pick up where I left off' : 'Complete agreement'}
              </button>
              <button
                onClick={() => { window.location.href = '/doorlypro/dashboard'; }}
                style={{ padding: '11px 18px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Back
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  const descriptions = (schema.flow && schema.flow.formatDescriptions) || {};
  return (
    <DoorlyProLayout>
      {overlayNode()}
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '32px 16px 80px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>Submit an Application</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 24px', lineHeight: 1.6 }}>
          Send Doorly a complete loan file. Your borrower still registers and confirms it before we can work it.
        </p>

        {/* ── 🚨 INFORMATIONAL. IT MUST NOT READ LIKE THE OVERLAY. ────────────
            The overlay BLOCKS: dimmed backdrop, dialog, nothing else usable.
            This does not block anything — the copy says so in as many words
            ("Nothing stops you submitting buyers in the meantime") and an LO who
            reads it as a second gate will stop submitting, which is the exact
            opposite of what it says.

            🔑 SO THE RENDERING CARRIES THAT, NOT ONLY THE WORDS. It sits IN the
            page flow rather than over it, in the muted amber this codebase uses
            for "in progress" rather than the red it uses for refusals, with no
            dimming, no dialog role, and nothing disabled beneath it. The form is
            immediately below and fully usable.

            ⚠️ task.not_yours IS THE CONDITION, and it is the server's. It marks
            a row whose remedy belongs to somebody else — rendering an actionable
            task this way would tell a partner to wait on something that is
            theirs to do. */}
        {countersignTask?.not_yours && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
                        padding: '14px 16px', marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#7a5200', marginBottom: 4 }}>
              {countersignTask.label}
            </div>
            {countersignTask.detail && (
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7 }}>
                {countersignTask.detail}
              </div>
            )}
            {/* The CTA only where there is something to press — an invite to
                send or resend. With a broker already in place there is no screen
                that helps, and offering one would be pretending otherwise. */}
            {countersignTask.cta && (
              <button
                onClick={() => { window.location.href = '/doorlypro/dashboard'; }}
                style={{ marginTop: 10, padding: '7px 14px', background: '#fff', color: '#7a5200',
                         border: '1px solid #fde68a', borderRadius: 7, fontWeight: 700,
                         fontSize: 12, cursor: 'pointer' }}>
                {countersignTask.cta}
              </button>
            )}
          </div>
        )}

        <SubmissionTabs tab={tab} onChange={setTab} drafts={drafts.length} submitted={list.length} />

        {tab === 'new' && (
          <div data-testid="lo-start">
            {/* 🔑 TWO START CARDS, ONE CARD EACH: a title and a description, nothing nested. A format closed pending
                review (MISMO 3.4) is greyed, not selectable, with "Coming soon". */}
            <FormatPicker formats={schema.formats} value={format} onChange={setFormat} descriptions={descriptions} />
            {startErr && <p className="lo-refusal" style={{ marginBottom: 12 }}>{startErr}</p>}
            <button type="button" className="lo-btn lo-btn-primary" data-testid="lo-start-button" disabled={starting || agreementBlocked || format !== 'manual'} onClick={start}>
              {starting ? 'Starting\u2026' : 'Start'}
            </button>
          </div>
        )}

        {tab === 'drafts' && <DraftsList drafts={drafts} onContinue={(d) => setParams({ draft: String(d.id), step: d.step_key })} />}
        {tab === 'submitted' && <SentList list={list} onOpen={(s) => navigate(`/doorlypro/submissions/${s.id}`)} />}
      </div>
    </DoorlyProLayout>
  );
}

// 🔑 TWO START CARDS, ONE CARD EACH: a title and a description, nothing nested. A format closed pending review
// (MISMO 3.4) is greyed, disabled, out of the tab order, with "Coming soon".
function FormatPicker({ formats, value, onChange, descriptions }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 20 }}>
      {(formats || []).map((f) => (
        <button key={f.key} type="button" disabled={!f.available} aria-disabled={!f.available} tabIndex={f.available ? 0 : -1}
          aria-pressed={value === f.key && f.available ? 'true' : 'false'} data-testid={`format-card-${f.key}`}
          onClick={() => f.available && onChange(f.key)}
          style={{ textAlign: 'left', padding: '16px 18px', borderRadius: 12, cursor: f.available ? 'pointer' : 'not-allowed',
            border: `2px solid ${value === f.key && f.available ? SELECTED : '#e5e7eb'}`, background: f.available ? '#fff' : '#f7f7f7', opacity: f.available ? 1 : 0.75 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: f.available ? '#111827' : '#6b7280' }}>{f.label}</div>
          {descriptions[f.key] && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{descriptions[f.key]}</div>}
          {!f.available && f.pendingReview ? (
            <span data-testid={`format-coming-soon-${f.key}`} style={{ display: 'inline-block', marginTop: 8, padding: '2px 8px', borderRadius: 999,
              fontSize: 11, fontWeight: 700, background: '#e6e6e6', color: '#1A3A6B' }}>Coming soon</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

// 🔑 THE DRAFTS TAB: the borrower's name, "In progress · N of M · <step>", Continue, and "Deleted <date> if not continued".
function DraftsList({ drafts, onContinue }) {
  if (!drafts.length) return <p style={{ fontSize: 14, color: '#6b7280', padding: '20px 0' }}>No drafts. A submission you start is saved here until you send it.</p>;
  return (
    <div style={{ display: 'grid', gap: 10 }} data-testid="lo-drafts">
      {drafts.map((d) => (
        <div key={d.id} data-testid="lo-draft-row" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{d.borrower}</div>
            <div data-testid="lo-draft-progress" style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{d.progress}</div>
            <div data-testid="lo-draft-deletes" style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Deleted {dateLabel(d.deletes_on)} if not continued</div>
          </div>
          <button type="button" className="lo-btn lo-btn-primary" data-testid="lo-draft-continue" style={{ height: 40, padding: '0 18px', fontSize: 14 }}
            onClick={() => onContinue(d)}>Continue</button>
        </div>
      ))}
    </div>
  );
}

// 🔑 THE SUBMITTED TAB: the DoorlyPro status, "N days before it expires" only before the borrower registers. The
// consent pill and its line are gone. A row opens the file's page.
function SentList({ list, onOpen }) {
  if (!list.length) return <p style={{ fontSize: 14, color: '#6b7280', padding: '20px 0' }}>Nothing submitted yet.</p>;
  return (
    <div style={{ display: 'grid', gap: 10 }} data-testid="lo-submitted">
      {list.map((s) => (
        <button key={s.id} type="button" data-testid="lo-submitted-row" onClick={() => onOpen(s)}
          style={{ textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, cursor: 'pointer', font: 'inherit' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{s.borrower}</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, overflowWrap: 'anywhere' }}>{s.email}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700,
                color: s.state === 'with_borrower' ? 'var(--color-success-text)' : s.state === 'expired' ? '#b3321f' : '#6b7280' }}>{s.label}</div>
              {s.days_left !== null && s.state === 'waiting_on_borrower' && (
                <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 3 }}>{s.days_left} day{s.days_left === 1 ? '' : 's'} before it expires</div>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
